import { cn } from '@/lib/utils';

export interface ToggleButtonOption<T extends string = string> {
  value: T;
  label: string;
}

export interface ToggleButtonGroupProps<T extends string = string> {
  options: readonly ToggleButtonOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name when there is no visible label; otherwise use aria-labelledby. */
  ariaLabel?: string;
  /** Id of the element that names the group (e.g. a visible label). */
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  disabled?: boolean;
  className?: string;
}

/** Single-choice segmented control: one pressed button at a time. */
const ToggleButtonGroup = <T extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
  className,
  ...aria
}: ToggleButtonGroupProps<T>) => {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      {...aria}
      className={cn('inline-flex flex-wrap gap-1 rounded-md border border-border bg-muted p-1', className)}
    >
      {options.map((option) => {
        const pressed = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'min-h-9 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-muted',
              'disabled:cursor-not-allowed disabled:opacity-50',
              pressed
                ? 'border border-border bg-background text-foreground shadow-sm'
                : 'border border-transparent text-muted-foreground hover:bg-background/50 hover:text-foreground'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default ToggleButtonGroup;
